import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  Link as MuiLink,
  Alert,
  InputAdornment,
  IconButton,
  Divider
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Email as EmailIcon,
  Lock as LockIcon
} from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { motion } from 'framer-motion';

import { useAuth } from '../../context/AuthContext';

const Login = () => {
  const navigate = useNavigate();
  const { login, error, resendVerification } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isResendingVerification, setIsResendingVerification] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors }
  } = useForm();

  const emailValue = watch('email', '');

  const onSubmit = async (data) => {
    // Prevent multiple submissions
    if (isLoading) {
      return;
    }
    
    setIsLoading(true);
    try {
      const result = await login(data.email, data.password);
      
      if (result.success) {
        navigate('/');
      }
    } catch (error) {
      // Error is already handled in login function
      console.error('Login error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleResendVerification = async () => {
    const email = emailValue.trim();
    if (!email) {
      return;
    }

    setIsResendingVerification(true);
    await resendVerification(email);
    setIsResendingVerification(false);
  };

  return (
    <Box sx={{ maxWidth: 400, mx: 'auto' }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            mb: 1,
            color: 'text.primary'
          }}
        >
          Welcome back
        </Typography>
        <Typography
          variant="body1"
          sx={{
            mb: 4,
            color: 'text.secondary'
          }}
        >
          Sign in to manage your brands and content.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Box
          component="form" 
          onSubmit={(e) => {
            e.preventDefault();
            if (!isLoading) {
              handleSubmit(onSubmit)(e);
            }
          }}
        >
          <TextField
            fullWidth
            label="Email Address"
            type="email"
            autoComplete="email"
            {...register('email', {
              required: 'Email is required',
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: 'Invalid email address'
              }
            })}
            error={!!errors.email}
            helperText={errors.email?.message}
            sx={{ mb: 3 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon color="action" />
                </InputAdornment>
              ),
            }}
          />

          <TextField
            fullWidth
            label="Password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="current-password"
            {...register('password', {
              required: 'Password is required',
              minLength: {
                value: 6,
                message: 'Password must be at least 6 characters'
              }
            })}
            error={!!errors.password}
            helperText={errors.password?.message}
            sx={{ mb: 3 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleShowPassword}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
            <MuiLink
              component="button"
              type="button"
              variant="body2"
              disabled={isResendingVerification || isLoading}
              onClick={handleResendVerification}
              sx={{ textDecoration: 'none', background: 'none', border: 0, p: 0, cursor: 'pointer', color: 'text.secondary' }}
            >
              {isResendingVerification ? 'Sending verification…' : 'Resend verification email'}
            </MuiLink>
            <MuiLink
              component={Link}
              to="/forgot-password"
              variant="body2"
              sx={{ textDecoration: 'none', fontWeight: 600 }}
            >
              Forgot your password?
            </MuiLink>
          </Box>

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={isLoading}
            sx={{
              mb: 3,
              py: 1.5,
              fontSize: '1rem',
              fontWeight: 600,
              '&:disabled': {
                opacity: 0.6
              }
            }}
            onClick={(e) => {
              // Prevent double submission
              if (isLoading) {
                e.preventDefault();
                return false;
              }
            }}
          >
            {isLoading ? 'Signing In...' : 'Sign In'}
          </Button>

          <Divider sx={{ my: 3 }}>
            <Typography variant="body2" color="text.secondary">
              OR
            </Typography>
          </Divider>

          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Don't have an account?{' '}
              <MuiLink
                component={Link}
                to="/register"
                sx={{
                  fontWeight: 600,
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline'
                  }
                }}
              >
                Sign up here
              </MuiLink>
            </Typography>
          </Box>
        </Box>
      </motion.div>
    </Box>
  );
};

export default Login;





